/**
 * Name Input Component
 *
 * Input for name fields that only allows letters and spaces.
 * - Prevents numbers and special characters at input level
 * - Shows validation error if invalid characters are attempted
 */

import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { filterNameInput, NAME_REGEX } from '@/lib/validation';

export interface NameInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label?: string;
  error?: string;
  onChange: (value: string) => void;
  value: string;
}

export const NameInput = forwardRef<HTMLInputElement, NameInputProps>(
  ({ className, label, error, onChange, value, id, required, ...props }, ref) => {
    const [showInvalidWarning, setShowInvalidWarning] = useState(false);
    const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;

      // Check if input contains invalid characters
      if (!NAME_REGEX.test(rawValue)) {
        // Show warning briefly
        setShowInvalidWarning(true);
        setTimeout(() => setShowInvalidWarning(false), 2000);
      }

      // Filter out invalid characters and pass to parent
      const filtered = filterNameInput(rawValue);
      onChange(filtered);
    };

    const displayError = error || (showInvalidWarning ? 'Nur Buchstaben, Leerzeichen und Bindestriche erlaubt' : undefined);

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-slate-300 mb-1.5">
            {label}
            {required && <span className="text-red-400 ml-1">*</span>}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          type="text"
          value={value}
          onChange={handleChange}
          autoComplete="off"
          className={cn(
            'w-full px-3 py-2 rounded-lg text-sm',
            'bg-slate-700 border border-slate-600 text-slate-100',
            'placeholder:text-slate-400',
            'focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            displayError && 'border-red-500 focus:ring-red-500',
            className
          )}
          {...props}
        />
        {displayError && (
          <p className={cn(
            'mt-1.5 text-sm',
            showInvalidWarning && !error ? 'text-orange-400' : 'text-red-400'
          )}>
            {displayError}
          </p>
        )}
      </div>
    );
  }
);

NameInput.displayName = 'NameInput';
